import { useMutation, useQuery, useQueryClient } from "@/lib/query";
import { Loader2, Plus, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { doctorApi, doctorKeys } from "@/lib/api/doctorApi";
import { ApiError } from "@/lib/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { WEEKDAYS, type Doctor, type RegisterDoctorInput, type ScheduleSlot } from "@/types/doctor";

interface FormState {
  name: string;
  specialization: string;
  experience: string;
  fees: string;
  location: string;
  hospitalName: string;
  phone: string;
  bio: string;
  profilePicture: string;
  videoConsultationAvailable: boolean;
  qualifications: string[];
  languages: string[];
  schedule: ScheduleSlot[];
}

function initialState(doctor?: Doctor): FormState {
  return {
    name: doctor?.name ?? "",
    specialization: doctor?.specialization ?? "",
    experience: doctor ? String(doctor.experience) : "",
    fees: doctor ? String(doctor.fees) : "",
    location: doctor?.location ?? "",
    hospitalName: doctor?.hospitalName ?? "",
    phone: doctor?.phone ?? "",
    bio: doctor?.bio ?? "",
    profilePicture: doctor?.profilePicture ?? "",
    videoConsultationAvailable: doctor?.videoConsultationAvailable ?? false,
    qualifications: doctor?.qualifications ?? [],
    languages: doctor?.languages ?? [],
    schedule: doctor?.schedule ?? [],
  };
}

function TagField({
  id,
  label,
  placeholder,
  values,
  onChange,
}: {
  id: string;
  label: string;
  placeholder: string;
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState("");

  const add = () => {
    const value = draft.trim();
    if (!value || values.includes(value)) return setDraft("");
    onChange([...values, value]);
    setDraft("");
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-2">
        <Input
          id={id}
          value={draft}
          placeholder={placeholder}
          className="h-11"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button type="button" variant="outline" className="h-11 shrink-0" onClick={add}>
          <Plus className="size-4" /> Add
        </Button>
      </div>
      {values.length > 0 && (
        <ul className="flex flex-wrap gap-2 pt-1">
          {values.map((value) => (
            <li key={value}>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                {value}
                <button
                  type="button"
                  aria-label={`Remove ${value}`}
                  className="text-muted-foreground transition-colors hover:text-destructive"
                  onClick={() => onChange(values.filter((item) => item !== value))}
                >
                  <X className="size-3" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ScheduleEditor({
  schedule,
  onChange,
}: {
  schedule: ScheduleSlot[];
  onChange: (next: ScheduleSlot[]) => void;
}) {
  const slotFor = (day: string) => schedule.find((slot) => slot.day === day);

  const update = (day: string, patch: Partial<ScheduleSlot>) => {
    const existing = slotFor(day);
    if (!existing) {
      onChange([
        ...schedule,
        { day, startTime: "09:00", endTime: "17:00", isAvailable: true, ...patch },
      ]);
      return;
    }
    onChange(schedule.map((slot) => (slot.day === day ? { ...slot, ...patch } : slot)));
  };

  return (
    <div className="space-y-2">
      {WEEKDAYS.map((day) => {
        const slot = slotFor(day);
        const on = slot?.isAvailable ?? false;
        return (
          <div
            key={day}
            className={cn(
              "flex flex-wrap items-center gap-3 rounded-xl border border-border px-4 py-3 transition-colors",
              on ? "bg-card" : "bg-muted/40",
            )}
          >
            <Switch
              id={`day-${day}`}
              checked={on}
              onCheckedChange={(checked) => update(day, { isAvailable: checked })}
            />
            <Label htmlFor={`day-${day}`} className="w-24 cursor-pointer text-sm font-medium">
              {day}
            </Label>
            <div className="ml-auto flex items-center gap-2">
              <Input
                type="time"
                aria-label={`${day} start time`}
                value={slot?.startTime ?? "09:00"}
                disabled={!on}
                onChange={(e) => update(day, { startTime: e.target.value })}
                className="h-9 w-[7.5rem]"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="time"
                aria-label={`${day} end time`}
                value={slot?.endTime ?? "17:00"}
                disabled={!on}
                onChange={(e) => update(day, { endTime: e.target.value })}
                className="h-9 w-[7.5rem]"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <section className="card-surface p-6 sm:p-7">
      <h2 className="font-display text-lg font-semibold">{title}</h2>
      {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      <div className="mt-6 space-y-5">{children}</div>
    </section>
  );
}

export function DoctorForm({
  mode,
  doctor,
  onSuccess,
}: {
  mode: "register" | "edit";
  doctor?: Doctor | undefined;
  onSuccess?: (() => void) | undefined;
}) {
  const [form, setForm] = useState<FormState>(() => initialState(doctor));
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { uid } = useAuth();

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const mutation = useMutation({
    mutationFn: (input: RegisterDoctorInput) =>
      mode === "register" ? doctorApi.register(input) : doctorApi.updateMe(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: doctorKeys.me });
      toast.success(
        mode === "register"
          ? "Registration submitted for verification."
          : "Your profile has been updated.",
      );
      onSuccess?.();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "We couldn't save your details.");
    },
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const experience = Number(form.experience);
    if (!form.name.trim() || !form.specialization.trim()) {
      setError("Name and specialization are required.");
      return;
    }
    if (!Number.isFinite(experience) || experience < 0) {
      setError("Enter your years of experience as a number.");
      return;
    }

    mutation.mutate({
      name: form.name.trim(),
      specialization: form.specialization.trim(),
      experience,
      fees: form.fees ? Number(form.fees) : 0,
      location: form.location.trim(),
      hospitalName: form.hospitalName.trim() || null,
      phone: form.phone.trim() || null,
      bio: form.bio.trim() || null,
      profilePicture: form.profilePicture.trim() || null,
      videoConsultationAvailable: form.videoConsultationAvailable,
      qualifications: form.qualifications,
      languages: form.languages,
      schedule: form.schedule.filter((slot) => slot.isAvailable),
    });
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6" noValidate>
      <Section title="About you" description="This is what patients see on your HealNova profile.">
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="name">Full name</Label>
            <Input
              id="name"
              required
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Dr. Aditi Sharma"
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="specialization">Specialization</Label>
            <Input
              id="specialization"
              required
              value={form.specialization}
              onChange={(e) => set("specialization", e.target.value)}
              placeholder="Cardiology"
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="experience">Years of experience</Label>
            <Input
              id="experience"
              type="number"
              min={0}
              required
              value={form.experience}
              onChange={(e) => set("experience", e.target.value)}
              placeholder="12"
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fees">Consultation fee (INR)</Label>
            <Input
              id="fees"
              type="number"
              min={0}
              value={form.fees}
              onChange={(e) => set("fees", e.target.value)}
              placeholder="800"
              className="h-11"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="bio">Short bio</Label>
          <Textarea
            id="bio"
            rows={4}
            value={form.bio}
            onChange={(e) => set("bio", e.target.value)}
            placeholder="A couple of sentences about your practice and approach to care."
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="profilePicture">Profile photo URL</Label>
          <Input
            id="profilePicture"
            type="url"
            value={form.profilePicture}
            onChange={(e) => set("profilePicture", e.target.value)}
            placeholder="https://…"
            className="h-11"
          />
        </div>
      </Section>

      <Section title="Practice details">
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="hospitalName">Hospital or clinic</Label>
            <Input
              id="hospitalName"
              value={form.hospitalName}
              onChange={(e) => set("hospitalName", e.target.value)}
              placeholder="HealNova Care Centre"
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={form.location}
              onChange={(e) => set("location", e.target.value)}
              placeholder="Bengaluru, India"
              className="h-11"
            />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="phone">Contact number</Label>
            <Input
              id="phone"
              type="tel"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              placeholder="+91 98765 43210"
              className="h-11"
            />
          </div>
        </div>

        <TagField
          id="qualifications"
          label="Qualifications"
          placeholder="MBBS, MD (Cardiology)…"
          values={form.qualifications}
          onChange={(next) => set("qualifications", next)}
        />
        <TagField
          id="languages"
          label="Languages spoken"
          placeholder="English, Hindi…"
          values={form.languages}
          onChange={(next) => set("languages", next)}
        />

        <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-muted/40 px-4 py-3">
          <div>
            <Label htmlFor="video" className="cursor-pointer text-sm font-medium">
              Video consultations
            </Label>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Let patients book remote appointments with you.
            </p>
          </div>
          <Switch
            id="video"
            checked={form.videoConsultationAvailable}
            onCheckedChange={(checked) => set("videoConsultationAvailable", checked)}
          />
        </div>
      </Section>

      <Section title="Weekly availability" description="Turn on the days you consult.">
        <ScheduleEditor schedule={form.schedule} onChange={(next) => set("schedule", next)} />
      </Section>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end">
        <Button
          type="submit"
          size="lg"
          className="h-12 px-8 text-base"
          disabled={mutation.isPending || !uid}
        >
          {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
          {mode === "register" ? "Submit for verification" : "Save changes"}
        </Button>
      </div>
    </form>
  );
}

/** Convenience hook so the register route can detect an existing profile. */
export function useExistingDoctor(uid: string | null) {
  return useQuery({
    queryKey: [...doctorKeys.me, uid],
    queryFn: () => doctorApi.me(),
    enabled: Boolean(uid),
    retry: false,
  });
}
